import React from 'react';
import { Play, Star, Zap, Trophy, Users } from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { GameTemplate } from '../types/game';
import { staggerContainer, fadeInUp } from '../constants/game';

interface HomeScreenProps {
  gameTemplates: GameTemplate[];
  favoriteTemplates: string[];
  onStartNewGame: (template?: GameTemplate) => void;
  onToggleFavorite: (templateId: string) => void;
  onNavigateToGames: () => void;
}

const QuickStartCard = ({ onStartNewGame }: { onStartNewGame: () => void }) => (
  <motion.div
    variants={fadeInUp}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    transition={{ type: "spring", stiffness: 400, damping: 17 }}
  >
    <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 cursor-pointer">
      <div className="flex items-center gap-4">
        <div className="p-3 bg-blue-100 rounded-full">
          <Zap className="w-6 h-6 text-blue-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-blue-900 mb-1">Quick Start</h3>
          <p className="text-sm text-blue-700 mb-3">
            Start a new T20 match with standard settings
          </p>
          <Button 
            onClick={onStartNewGame}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Play className="w-4 h-4 mr-2" />
            Start Game
          </Button>
        </div>
      </div>
    </Card>
  </motion.div>
);

const FavoriteTemplateCard = ({ 
  template, 
  onStartNewGame, 
  onToggleFavorite 
}: { 
  template: GameTemplate;
  onStartNewGame: (template: GameTemplate) => void;
  onToggleFavorite: (templateId: string) => void;
}) => (
  <motion.div
    variants={fadeInUp}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    transition={{ type: "spring", stiffness: 400, damping: 17 }}
  >
    <Card className="p-4 relative group">
      {/* Favorite star */}
      <motion.button
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite(template.id);
        }}
        className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-100 transition-colors"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
      </motion.button>

      <div className="space-y-3">
        <div>
          <h4 className="font-semibold text-gray-900 pr-8">{template.name}</h4>
          <p className="text-sm text-gray-600">
            {template.oversPerInnings} overs • {template.numberOfPlayers} players
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {template.customRules.enableRetirement && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
              Retirement at {template.customRules.retirementRuns}
            </span>
          )}
          {template.customRules.enableLastManStands && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
              Last Man Stands
            </span>
          )}
          {template.customRules.enableMaxBallsPerOver && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-700">
              Max {template.customRules.maxBallsPerOver} balls
            </span>
          )}
        </div>

        <Button 
          onClick={() => onStartNewGame(template)}
          size="sm"
          variant="outline"
          className="w-full"
        >
          <Play className="w-4 h-4 mr-2" />
          Use Template
        </Button>
      </div>
    </Card>
  </motion.div>
);

export function HomeScreen({
  gameTemplates,
  favoriteTemplates,
  onStartNewGame,
  onToggleFavorite,
  onNavigateToGames,
}: HomeScreenProps) {
  const favoriteTemplatesData = gameTemplates.filter(template => 
    favoriteTemplates.includes(template.id)
  );

  return (
    <div className="bg-white min-h-screen flex flex-col w-full max-w-[800px] mx-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white z-40 border-b border-gray-100">
        <div className="p-4">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h1 className="text-2xl font-bold text-gray-900">Cricket Scorer</h1>
            <p className="text-gray-600 mt-1">Ready to start your next match?</p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <motion.div
          className="p-4 space-y-6"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          {/* Quick Start Section */}
          <div>
            <motion.h2 
              className="text-lg font-semibold text-gray-900 mb-4"
              variants={fadeInUp}
            >
              Quick Start
            </motion.h2>
            <QuickStartCard onStartNewGame={() => onStartNewGame()} />
          </div>

          {/* Favorite Templates Section */}
          {favoriteTemplatesData.length > 0 && (
            <div>
              <motion.div 
                className="flex items-center justify-between mb-4"
                variants={fadeInUp}
              >
                <h2 className="text-lg font-semibold text-gray-900">
                  Favorite Templates
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onNavigateToGames}
                  className="text-blue-600 hover:text-blue-700"
                >
                  View All
                </Button>
              </motion.div>
              
              <div className="grid gap-3">
                {favoriteTemplatesData.slice(0, 3).map((template) => (
                  <FavoriteTemplateCard
                    key={template.id}
                    template={template}
                    onStartNewGame={onStartNewGame}
                    onToggleFavorite={onToggleFavorite}
                  />
                ))}
              </div>

              {favoriteTemplatesData.length > 3 && (
                <motion.div 
                  className="mt-4 text-center"
                  variants={fadeInUp}
                >
                  <Button
                    variant="outline"
                    onClick={onNavigateToGames}
                  >
                    <Star className="w-4 h-4 mr-2" />
                    View All {favoriteTemplatesData.length} Favorites
                  </Button>
                </motion.div>
              )}
            </div>
          )}

          {/* Getting Started Section (if no favorites) */}
          {favoriteTemplatesData.length === 0 && (
            <motion.div 
              className="text-center py-8"
              variants={fadeInUp}
            >
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-full inline-block">
                  <Trophy className="w-8 h-8 text-gray-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">No Favorite Templates</h3>
                  <p className="text-gray-600 mb-4">
                    Star your favorite game templates to see them here for quick access.
                  </p>
                  <Button
                    onClick={onNavigateToGames}
                    variant="outline"
                  >
                    <Trophy className="w-4 h-4 mr-2" />
                    Browse Game Templates
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Additional Quick Actions */}
          <motion.div 
            className="grid grid-cols-2 gap-3"
            variants={fadeInUp}
          >
            <Button
              variant="outline"
              onClick={onNavigateToGames}
              className="h-16 flex flex-col gap-1"
            >
              <Trophy className="w-5 h-5" />
              <span className="text-sm">Games</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => onNavigateToGames()} // This will open games, user can access teams from there
              className="h-16 flex flex-col gap-1"
            >
              <Users className="w-5 h-5" />
              <span className="text-sm">Teams</span>
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom padding for navigation */}
      <div className="h-20 bg-white" />
    </div>
  );
}